/*
 * Instant size switching on the vase page.
 *
 * The sizes of one vase are separate products (Maya Small Vase, Maya Large Vase), so a
 * size pill is a link to another product page. To make it behave like a variant, the
 * other size's sections are fetched ahead of time through the Section Rendering API and
 * swapped in on click: the theme renders them exactly as it would on that product's own
 * page — gallery, price, buy button, spec sheet — and nothing is redrawn here by hand.
 * The other size's first photographs are preloaded, so the swap shows them at once.
 *
 * Without this script, or if a fetch fails, the pill is an ordinary link.
 */
if (!customElements.get('product-size-switch')) {
  // Swapped per size: the product section and the sections below it that depend on the
  // product. The Section Rendering API takes at most five sections per request.
  const SWAP_SECTIONS = [
    '.shopify-section:has([data-main-product])',
    '.shopify-section.section-product-spec-sheet',
    '.shopify-section.section-product-video',
    '.shopify-section.section-product-spec-row',
  ];
  const PRELOAD_IMAGES = 3;

  const cache = new Map(); // handle -> Promise<{handle, title, url, sections: {id: html}}>
  const sizeOf = (pill) => ({handle: pill.dataset.handle, url: pill.dataset.url, title: pill.dataset.title});

  // A section links its own stylesheets (main-product links section-product.css). Swapping
  // the section drops that <link> and inserts a new one, and until the new one is applied
  // the product has no CSS: the page flashes unstyled, and anything that measures itself
  // on insert (the Read more clamp) measures the wrong layout. So every stylesheet the
  // swapped sections use is also kept in <head>, loaded before any swap happens.
  const styles = new Map(); // href -> Promise
  const ensureStyle = (href) => {
    if (styles.has(href)) return styles.get(href);
    let link = [...document.head.querySelectorAll('link[rel="stylesheet"]')].find((l) => l.getAttribute('href') === href);
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.append(link);
    }
    const ready = link.sheet ? Promise.resolve() : new Promise((resolve) => {
      link.addEventListener('load', resolve, {once: true});
      link.addEventListener('error', resolve, {once: true});
    });
    styles.set(href, ready);
    return ready;
  };
  const stylesOf = (root) => [...root.querySelectorAll('link[rel="stylesheet"]')].map((l) => l.getAttribute('href')).filter(Boolean);

  const sectionIds = () => {
    const ids = [];
    for (const selector of SWAP_SECTIONS) {
      for (const el of document.querySelectorAll(selector)) {
        const id = el.id.replace('shopify-section-', '');
        if (id && !ids.includes(id)) ids.push(id);
      }
    }
    return ids.slice(0, 5);
  };

  // Every size, the first one included, is fetched from the server rather than copied from
  // the page: the gallery script has already rewritten its own markup by then.
  const load = ({handle, url, title}) => {
    if (cache.has(handle)) return cache.get(handle);
    const ids = sectionIds();
    const request = fetch(`${url}${url.includes('?') ? '&' : '?'}sections=${ids.join(',')}`, {credentials: 'same-origin'})
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(async (sections) => {
        const main = ids.find((id) => sections[id]?.includes('data-main-product'));
        // A stale preview link can answer with another product; never swap that in.
        if (!main || !sections[main].includes(`data-product-handle="${handle}"`)) throw new Error('unexpected product');
        preload(sections[main]);
        const hrefs = Object.values(sections).flatMap((html) => stylesOf(new DOMParser().parseFromString(html, 'text/html')));
        await Promise.all(hrefs.map(ensureStyle));
        return {handle, title, url, sections};
      });
    request.catch(() => cache.delete(handle));
    cache.set(handle, request);
    return request;
  };

  const preload = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const images = [...doc.querySelectorAll('.product-grid__item--media img')].slice(0, PRELOAD_IMAGES);
    for (const source of images) {
      const image = new Image();
      if (source.sizes) image.sizes = source.sizes;
      if (source.srcset) image.srcset = source.srcset;
      image.src = source.getAttribute('src');
    }
  };

  const apply = (entry) => {
    for (const [id, html] of Object.entries(entry.sections)) {
      const current = document.getElementById(`shopify-section-${id}`);
      if (!current) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
      if (fresh) current.replaceChildren(...[...fresh.childNodes].map((node) => document.importNode(node, true)));
    }
    // The theme's sticky add-to-cart bar watches the buy buttons it found on page load.
    // Inline scripts do not run again after a swap, so point that watcher at the new ones.
    try {
      const buttons = document.querySelector('.product-form__buttons');
      // eslint-disable-next-line no-undef
      if (buttons && typeof observer !== 'undefined' && observer instanceof IntersectionObserver) observer.observe(buttons);
    } catch (error) { /* the bar is optional */ }
    const suffix = document.title.includes(' – ') ? document.title.slice(document.title.lastIndexOf(' – ')) : '';
    document.title = `${entry.title}${suffix}`;
    document.dispatchEvent(new CustomEvent('product-size:switched', {detail: {handle: entry.handle, url: entry.url}}));
  };

  const go = async (pill) => {
    const switcher = pill.closest('product-size-switch');
    switcher?.setAttribute('aria-busy', 'true');
    let entry;
    try {
      entry = await load(sizeOf(pill));
    } catch (error) {
      window.location.assign(pill.href);
      return;
    } finally {
      switcher?.removeAttribute('aria-busy');
    }
    apply(entry);
    // The address bar is a nicety: if it cannot be updated, the switch has still happened.
    try { history.pushState({productSize: sizeOf(pill)}, '', entry.url); } catch (error) { /* keep the page */ }
  };

  window.addEventListener('popstate', async (event) => {
    const size = event.state?.productSize;
    if (!size?.handle) return;
    try { apply(await load(size)); } catch (error) { window.location.reload(); }
  });

  customElements.define('product-size-switch', class extends HTMLElement {
    connectedCallback() {
      for (const id of sectionIds()) {
        const section = document.getElementById(`shopify-section-${id}`);
        if (section) stylesOf(section).forEach(ensureStyle);
      }
      const current = this.querySelector('a[data-handle][aria-current]');
      if (current && !history.state?.productSize) history.replaceState({...history.state, productSize: sizeOf(current)}, '');
      const others = [...this.querySelectorAll('a[data-handle]:not([aria-current])')];
      for (const pill of others) {
        pill.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          go(pill);
        });
        pill.addEventListener('pointerenter', () => load(sizeOf(pill)).catch(() => {}), {once: true});
      }
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 800));
      idle(() => others.forEach((pill) => load(sizeOf(pill)).catch(() => {})));
    }
  });
}
